<template>
  <div>
    <h2 class="text-left alert" :class="[removed ? 'alert-danger' : 'alert-success']">
      Current Mastitis Situation
      <a class="btn btn-sm" @click="$emit('onRemoveSection', stepName)">
        <span class="glyphicon" :class="[removed ? 'glyphicon-plus' : 'glyphicon-remove']"></span>
      </a>
    </h2>

    <template v-if="!removed">
      <subsection title="Historic BMSCC" :step-name="stepName" subsection="historic_bmscc">
        <table class="table table-bordered">
          <thead>
            <tr>
              <th>Shed/Herd</th>
              <th>Season</th>
              <th>Average BMSCC</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(bmscc, index) in model.CHARTS.dc2_bmscc_table" :key="`bmscc-${index}`">
              <td class="text-left">{{ bmscc['Shed/Herd'] }}</td>
              <td class="text-left">{{ bmscc['Season'] }}</td>
              <td class="text-left">{{ bmscc['Average BMSCC'] }}</td>
            </tr>
          </tbody>
        </table>
      </subsection>

      <subsection
        :title="farm.farm_label"
        :subsection="`farm_${farm.id}-season_avg_bmscc`"
        v-for="farm in model.FARMS"
        :step-name="stepName"
        :key="`farm_${farm.id}-season_avg_bmscc`"
      >
        <report-comment :field_name="`farm_${farm.id}-season_avg_bmscc`" :model="model.COMMENTS" :heading="true">
        </report-comment>

        <dl class="dl-horizontal">
          <dt>Season Avg BMSCC</dt>
          <dd>{{ model[`farm_${farm.id}-season_avg_bmscc`] || '-' }}</dd>
        </dl>

        <dl class="dl-horizontal">
          <dt>Number of cell count grades</dt>
          <dd>{{ model[`farm_${farm.id}-num_cell_count_grades`] || '-' }}</dd>
        </dl>
      </subsection>

      <subsection
        :title="`Mastitis Treatments - ${farm.farm_label}`"
        :step-name="stepName"
        :subsection="`farm_${farm.id}-mastitis_treatments`"
        v-for="farm in model.FARMS"
        :key="`farm_${farm.id}-mastitis_treatments`"
      >
        <report-comment
          :field_name="`farm_${farm.id}-chart_mastitis_treatments`"
          :model="model.COMMENTS"
          :heading="true"
        >
        </report-comment>

        <p>Estimated number of cows treated per month based on purchases.</p>
        <div class="step-section">
          <bar
            :chart-data="treatmentsChartData(farm.id)"
            :options="treatmentsChartOptions()"
            :ref="'farm_' + farm.id + '-mastitis_treatments_chart'"
          >
          </bar>

          <table class="table table-bordered">
            <thead>
              <tr>
                <th></th>
                <th v-for="(date, index) in chartMastitisTreatmentsDates(farm.id)" :key="`date-${index}`">
                  {{ date }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(product, productIndex) in chartMastitisTreatmentsProducts(farm.id)"
                :key="`product-${productIndex}`"
              >
                <td>{{ product }}</td>
                <td v-for="(date, dateIndex) in chartMastitisTreatmentsDates(farm.id)" :key="dateIndex">
                  {{ getProductValueForDate(farm.id, product, date) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </subsection>

      <subsection title="Clinical Cases" :step-name="stepName" subsection="clinical_cases">
        <report-comment field_name="heading_clinical_cases" :model="model.COMMENTS" :heading="true"> </report-comment>
        <p>
          Estimated number of cows treated during different lactation periods as a percentage of total cows wintered.
        </p>
      </subsection>

      <subsection
        :title="`Clinical Cases - ${farm.farm_label}`"
        :step-name="stepName"
        :subsection="`farm_${farm.id}-clinical_cases`"
        v-for="farm in model.FARMS"
        :key="`farm_${farm.id}-clinical_cases`"
      >
        <table class="table table-bordered">
          <thead>
            <tr>
              <th></th>
              <th>Percent Cows Treated</th>
              <th>Region Avg. percent cows wintered</th>
              <th>Goal</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(caseData, index) in model.CHARTS.dc_mastitis_clinical_treatments_summ[farm.id]"
              :key="`caseIndex-${index}`"
            >
              <td class="text-left">
                {{ caseData['Period'] }}
              </td>
              <td class="text-left">
                {{ caseData['Percent Cows Treated'] }}
              </td>
              <td class="text-left">
                {{ caseData['Region Percent Cows Treated'] }}
              </td>
              <td class="text-left">
                {{ caseData['Goal'] }}
              </td>
            </tr>
          </tbody>
        </table>
      </subsection>

      <subsection
        title="Milk culture - Mastatest results"
        :step-name="stepName"
        subsection="milk_culture_mastatest_results"
      >
        <report-comment field_name="heading_mastatest_results" :model="model.COMMENTS" :heading="false">
        </report-comment>
        <div
          class="step-section"
          v-for="(farmResult, index) in model.CHARTS.dc_mastatest_results"
          :key="`farm_ma_result_${index}`"
        >
          <h3>{{ farmResult.farm_label }}</h3>
          <table class="table table-bordered">
            <thead>
              <tr>
                <th>Date</th>
                <th>In-house</th>
                <th>Cow number</th>
                <th>Quarter</th>
                <th>Pathogens present</th>
                <th>Antibiotic susceptibility</th>
                <th>Recommendation/Comments</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in farmResult.result" :key="`farm_ma_row_${index}`">
                <td class="text-left">{{ row['Date'] }}</td>
                <td class="text-left">{{ row['In-house'] }}</td>
                <td class="text-left">{{ row['Cow number'] }}</td>
                <td class="text-left">{{ row['Quarter'] }}</td>
                <td class="text-left" v-html="commaSeparatedValue(row['Pathogens present'])"></td>
                <td class="text-left" v-html="commaSeparatedValue(row['Antibiotic susceptibility'])"></td>
                <td class="text-left">{{ row['Recommendation/Comments'] }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </subsection>

      <subsection title="Calving Mastitis" :step-name="stepName" subsection="calving_mastitis">
        <report-comment field_name="heading_calving_mastitis" :model="model.COMMENTS" :heading="true"> </report-comment>

        <div v-for="farm in model.FARMS" class="text-left" :key="`calving_mastitis-${farm.id}`">
          <h4
            class="text-left"
            :class="isRemovedSubsection(`farm_${farm.id}-calving_mastitis`) ? 'comment-disabled' : ''"
          >
            {{ farm.farm_label }}
            <a class="btn btn-sm" @click="$emit('onRemoveSection', stepName, `farm_${farm.id}-calving_mastitis`)">
              <span
                class="glyphicon"
                :class="isRemovedSubsection(`farm_${farm.id}-calving_mastitis`) ? 'glyphicon-plus' : 'glyphicon-remove'"
              ></span>
            </a>
          </h4>

          <dl class="dl-horizontal">
            <dt>Of the {{ totalCowsTreated(farm.id) }} cows treated, what percentage were heifers?</dt>
            <dd>{{ model[`farm_${farm.id}-est_percent_mastitis_treat_is_heifers`] || '-' }}</dd>
          </dl>

          <dl class="dl-horizontal">
            <dt>Estimated % of treatments that were mixed age</dt>
            <dd>{{ estPercentMastitisTreatMixed(farm.id) || '-' }}</dd>
          </dl>
        </div>

        <br />

        <div class="step-section">
          <h3>Percentage of animal group treated for Mastitis</h3>
          <div v-for="farm in model.FARMS" :key="farm.id">
            <h4
              class="text-left"
              :class="
                isRemovedSubsection(`farm_${farm.id}-percentage_of_groups_treated_for_ma`) ? 'comment-disabled' : ''
              "
            >
              {{ farm.farm_label }}
              <a
                class="btn btn-sm"
                @click="$emit('onRemoveSection', stepName, `farm_${farm.id}-percentage_of_groups_treated_for_ma`)"
              >
                <span
                  class="glyphicon"
                  :class="
                    isRemovedSubsection(`farm_${farm.id}-percentage_of_groups_treated_for_ma`)
                      ? 'glyphicon-plus'
                      : 'glyphicon-remove'
                  "
                ></span>
              </a>
            </h4>

            <table class="table table-bordered">
              <thead>
                <tr>
                  <th></th>
                  <th>Animal Numbers</th>
                  <th>Number treated in spring previous season</th>
                  <th>Number treated in spring</th>
                  <th>Percent of group treated</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>total cows wintered</td>
                  <td>{{ totalCowsWintered(farm.id) }}</td>
                  <td>
                    {{ (model.CHARTS['dc_spring_mastitis_breakdown'][farm.id][0] || { prev_tot: 0 }).prev_tot }}
                  </td>
                  <td>{{ totalCowsTreated(farm.id) }}</td>
                  <td>
                    {{
                      (model.CHARTS['dc_spring_mastitis_breakdown'][farm.id][0] || { percent_treated: 0 })
                        .percent_treated
                    }}
                  </td>
                </tr>
                <tr>
                  <td>Mixed Age</td>
                  <td>
                    {{ (model.CHARTS['dc_spring_mastitis_breakdown'][farm.id][0] || { mixed_age: 0 }).mixed_age }}
                  </td>
                  <td>
                    {{ (model.CHARTS['dc_spring_mastitis_breakdown'][farm.id][0] || { prev_ma: 0 }).prev_ma }}
                  </td>
                  <td>{{ mixedAgeTreated(farm.id) }}</td>
                  <td>{{ mixedAgePercentTreated(farm.id) }}</td>
                </tr>
                <tr>
                  <td>Heifers</td>
                  <td>{{ (model.CHARTS['dc_spring_mastitis_breakdown'][farm.id][0] || { heifers: 0 }).heifers }}</td>
                  <td>
                    {{ (model.CHARTS['dc_spring_mastitis_breakdown'][farm.id][0] || { prev_hef: 0 }).prev_hef }}
                  </td>
                  <td>{{ heifersTreated(farm.id) }}</td>
                  <td>{{ heifersPercentTreated(farm.id) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </subsection>

      <subsection
        :title="`Protocol Notes - ${farm.farm_label}`"
        :subsection="`farm_${farm.id}-protocols_notes`"
        v-for="farm in model.FARMS"
        :key="farm.id"
      >
        <dl class="dl-horizontal">
          <dt>Notes on treatment protocols</dt>
          <dd>{{ model[`farm_${farm.id}-treatment_protocols_notes`] || '-' }}</dd>
        </dl>

        <dl class="dl-horizontal">
          <dt>Milk quality key points to be revised at winter consult</dt>
          <dd>{{ model[`farm_${farm.id}-milk_quality_key_points_for_wc`] || '-' }}</dd>
        </dl>
      </subsection>
    </template>
  </div>
</template>

<script>
import Bar from '@/components/charts/Bar'
import mixin from './mixin'

export default {
  name: 'CurrentMastitisSituation',
  mixins: [mixin],
  components: {
    Bar,
  },
  methods: {
    commaSeparatedValue: function (str) {
      let text = ''
      let splittedText = str.split(', ')

      if (!(splittedText.length == 1 && splittedText[0] == '')) {
        splittedText.forEach(function (item) {
          text += '<p>- ' + item + '</p>'
        })
      }
      return text
    },
    totalCowsTreated: function (farmId) {
      if (this.model.CHARTS['dc_spring_mastitis_breakdown'][farmId][0]) {
        return this.model.CHARTS['dc_spring_mastitis_breakdown'][farmId][0].cows_treated
      }
    },
    totalCowsWintered: function (farmId) {
      if (this.model.CHARTS.dc_spring_mastitis_breakdown[farmId][0]) {
        return this.model.CHARTS.dc_spring_mastitis_breakdown[farmId][0].cows_wintered_incl_r2
      }
    },
    estPercentMastitisTreatMixed: function (farmId) {
      if (this.model[`farm_${farmId}-est_percent_mastitis_treat_is_heifers`]) {
        return 100 - this.model[`farm_${farmId}-est_percent_mastitis_treat_is_heifers`]
      }
      return 0
    },
    mixedAgeTreated: function (farmId) {
      let cowsTreated = this.totalCowsTreated(farmId)

      return Math.round((this.estPercentMastitisTreatMixed(farmId) / 100) * cowsTreated, 1)
    },
    mixedAgePercentTreated: function (farmId) {
      let mixedAge

      if (this.model.CHARTS.dc_spring_mastitis_breakdown[farmId][0]) {
        mixedAge = this.model.CHARTS.dc_spring_mastitis_breakdown[farmId][0]['mixed_age']
      } else {
        mixedAge = 0
      }

      return Math.round((this.mixedAgeTreated(farmId) / mixedAge) * 100, 1)
    },
    heifersTreated: function (farmId) {
      let cowsTreated

      if (this.model.CHARTS.dc_spring_mastitis_breakdown[farmId][0]) {
        cowsTreated = this.model.CHARTS.dc_spring_mastitis_breakdown[farmId][0]['cows_treated']
      } else {
        cowsTreated = 0
      }

      return Math.round((this.model[`farm_${farmId}-est_percent_mastitis_treat_is_heifers`] / 100) * cowsTreated, 1)
    },
    heifersPercentTreated: function (farmId) {
      let heifers

      if (this.model.CHARTS.dc_spring_mastitis_breakdown[farmId][0]) {
        heifers = this.model.CHARTS.dc_spring_mastitis_breakdown[farmId][0]['heifers']
      } else {
        heifers = 0
      }
      return Math.round((this.heifersTreated(farmId) / heifers) * 100, 1)
    },
    updateCalvingMastitisValues: function (farmId) {
      let cowsTreated = this.totalCowsTreated(farmId)
      this.model['farm_' + farmId + '-mastitis_treated_spring_total'] = cowsTreated
      this.model['farm_' + farmId + '-mastitis_treated_spring_ma'] = this.mixedAgeTreated(farmId)
      this.model['farm_' + farmId + '-mastitis_treated_spring_heifers'] = this.heifersTreated(farmId)
    },
    chartMastitisTreatmentsDates: function (farmId) {
      return Array.from(new Set(this.model.CHARTS.dc_mastitis_treatments[farmId].map((x) => x['Date']))).reverse()
    },
    chartMastitisTreatmentsProducts: function (farmId) {
      return Array.from(new Set(this.model.CHARTS.dc_mastitis_treatments[farmId].map((x) => x['Product'])))
    },
    treatmentsChartData: function (farmId) {
      var self = this

      var dates = this.chartMastitisTreatmentsDates(farmId)
      var products = this.chartMastitisTreatmentsProducts(farmId)

      var datasets = []

      products.forEach(function (productName) {
        var product = {
          label: productName,
          backgroundColor: '#' + ((Math.random() * 0xffffff) << 0).toString(16),
          data: [],
        }
        dates.forEach(function (dateName) {
          product['data'].push(self.getProductValueForDate(farmId, productName, dateName))
        })

        datasets.push(product)
      })

      return {
        labels: dates,
        datasets: datasets,
      }
    },
    getProductValueForDate: function (farmId, product, dateName) {
      var value = this.model.CHARTS.dc_mastitis_treatments[farmId].filter(
        (x) => x['Date'] == dateName && x['Product'] == product
      )
      if (value.length > 0) {
        return value[0]['Cows Treated']
      }
      return 0
    },
    treatmentsChartOptions: function () {
      return {
        responsive: true,
        scales: {
          xAxes: [
            {
              stacked: true,
              categoryPercentage: 0.5,
              barPercentage: 1,
            },
          ],
          yAxes: [
            {
              stacked: true,
            },
          ],
        },
      }
    },
  },
}
</script>
